import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { Injectable, NgZone, OnDestroy, HostListener } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Router } from '@angular/router';
import { UserDetailsService } from 'libs/data/src/lib/services/user-details/user-details.service';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import * as jwt_decode from 'jwt-decode';
import { CookieService } from 'ngx-cookie-service';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { CookieHelperService } from 'libs/shared/src/lib/service/cookie-helper.service';
import { AuthFacade, DashboardFacade } from '@piqet-angular/data';
import Swal from 'sweetalert2';
import {
  CustomModalService,
  ExpireSoonDescriptionStr,
  ExpireSoonStr,
  SessionExpiredMessage,
  SIMULTANEOUS_LOGIN_DETECTED_TEXT,
  SIMULTANEOUS_LOGIN_DETECTED_TITLE,
  UserActivityTypeEnum,
} from '@piqet-angular/shared';
import { MenuService } from 'libs/features/src/lib/menu/menu.service';
import { AuthenticationService } from 'libs/data/src/lib/services/auth/authentication.service';
import { CommonService } from './common.service';
import { SessionSyncService } from './SessionSync.service';
import { environment } from 'apps/piqet-angular/src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { AdministratorService } from 'libs/data/src/lib/services/administrator/administrator.service';
import { JoyrideService } from 'ngx-joyride';
import { FeatureConfigService } from './feature-config.service';

@Injectable({
  providedIn: 'root',
})
export class SessionService implements OnDestroy {
  hubConnection!: HubConnection;
  private bc = new BroadcastChannel('session_channel');

  private tabId: string = Math.random().toString(36).substring(2, 15);
  public sessionExpiring = new BehaviorSubject<number>(0);
  public forcedLogout = new BehaviorSubject<boolean>(false);
  public loginFromOtherTab = new BehaviorSubject<boolean>(false);

  private activeTabs: Set<string> = new Set();
  private disableApiCall: boolean = false;
  private signalRUrl = environment.signalRUrl;
  private url = environment.url;
  private currentPageStartTime: Date | null = null;
  private currentPageId: number | null = null;
  deviceId: any;

  constructor(
    private router: Router,
    private userDetails: UserDetailsService,
    private swalService: SwalService,
    private cookieService: CookieService,
    private store: Store,
    private toast: ToastrService,
    private cookieHelper: CookieHelperService,
    private authFacade: AuthFacade,
    private menuService: MenuService,
    private dashBoardFacade: DashboardFacade,
    private authService: AuthenticationService,
    private commonService: CommonService,
    private sessionSyncService: SessionSyncService,
    private ngZone: NgZone,
    private http: HttpClient,
    private administratorService: AdministratorService,
    private model: CustomModalService,
    private joyrideService: JoyrideService,
    private featureConfigService: FeatureConfigService
  ) {
    this.deviceId = this.commonService.initDeviceId();

    if (sessionStorage.getItem('session_reloading')) {
      sessionStorage.removeItem('session_reloading');
      this.disableApiCall = true;

      setTimeout(() => {
        this.disableApiCall = false;
      }, 5000);
    }

    this.bc.onmessage = event => {
      this.handleBroadcastMessage(event.data);
    };

    this.registerTab();
  }

  onBeforeUnload(): void {
    if (this.activeTabs.size <= 1 && !this.disableApiCall) {
      const token = this.cookieService.get('token');
      if (token) {
        this.notifyLastTabClosing();
      }
    }
  }

  private registerTab() {
    this.bc.postMessage({
      type: 'tabOpened',
      tabId: this.tabId,
      timestamp: Date.now(),
    });

    this.activeTabs.add(this.tabId);

    this.bc.postMessage({
      type: 'getActiveTabs',
      tabId: this.tabId,
      timestamp: Date.now(),
    });
  }

  unregisterTab() {
    this.bc.postMessage({
      type: 'tabClosed',
      tabId: this.tabId,
      timestamp: Date.now(),
    });

    this.activeTabs.delete(this.tabId);
  }

  private notifyLastTabClosing() {
    const actionType = this.getNavigationType();
    if (this.disableApiCall || sessionStorage.getItem('session_reloading') === 'true') {
      return;
    }

    this.sessionSyncService.notifyLastTabClosed(this.deviceId, actionType).subscribe(
      (response: any) => {},
      (error: any) => console.error('Last tab close notification failed', error)
    );
  }

  private getNavigationType(): any {
    try {
      const navEntries = window.performance.getEntriesByType('navigation');
      if (navEntries && navEntries.length > 0) {
        if ((navEntries[0] as PerformanceNavigationTiming).type == 'reload') {
          return UserActivityTypeEnum.TabReloaded;
        } else {
          return UserActivityTypeEnum.InactiveTabDetected;
        }
      }
    } catch (e) {
      console.error('Error determining navigation type:', e);
    }
    return UserActivityTypeEnum.TabReloaded;
  }

  initializeConnection(deviceId: string) {
    this.hubConnection = new HubConnectionBuilder()
      .withUrl(`${this.signalRUrl}/hubs/session`, {
        withCredentials: true,
      })
      .withAutomaticReconnect()
      .build();

    this.hubConnection.on('ReceiveNewsNotification', (newsId: any) => {
      this.bc.postMessage({
        type: 'ReceiveNewsNotification',
        newsId,
        tabId: this.tabId,
        timestamp: Date.now(),
      });

      this.handleGetNewsById(newsId);
    });

    this.hubConnection.on('ReceiveSessionTermination', (message: string) => {
      this.bc.postMessage({
        type: 'sessionTerminated',
        message,
        tabId: this.tabId,
        timestamp: Date.now(),
      });

      localStorage.removeItem('token');
      localStorage.removeItem('deviceId');
      sessionStorage.clear();
      this.cookieService.delete('token');
      this.handleSessionTermination(message);
    });

    this.hubConnection.on('SessionTerminated', (message: string) => {
      this.bc.postMessage({
        type: 'forcedLogout',
        tabId: this.tabId,
        timestamp: Date.now(),
      });

      this.forcedLogout.next(true);
      this.clearSession();
      this.ngZone.run(() => {
        this.router.navigate(['/login']);
      });
    });

    this.hubConnection
      .start()
      .then(() => {
        this.hubConnection
          .invoke('RegisterConnection', deviceId)
          .catch(err => console.error('RegisterConnection error:', err));
      })
      .catch(err => console.error('Error connecting to SignalR hub', err));

    // Handle reconnection
    this.hubConnection.onreconnected(() => {
      this.hubConnection
        .invoke('RegisterConnection', deviceId)
        .catch(err => console.error('Re-registration error after reconnect:', err));
    });
  }

  async handleGetNewsById(newsId: any) {
    const res = await this.administratorService.getAdminNewsDetailsList(newsId);
    this.openAdditionalInfo(res.data);
  }

  openAdditionalInfo(item: any) {
    if (this.joyrideService.isTourInProgress() || Swal.isVisible()) {
      return;
    }

    this.model.invoke('AdditionalInfoNews', {
      initialState: {
        item,
      },
      class: 'modal-xl',
    });
  }

  notifyLoginSuccess(token: string, expiry: Date) {
    this.bc.postMessage({
      type: 'loginSuccess',
      tabId: this.tabId,
      timestamp: Date.now(),
      token: token,
      expiry: expiry,
    });
  }

  private handleBroadcastMessage(data: any) {
    if (data.tabId === this.tabId) return;

    switch (data.type) {
      case 'loginSuccess':
        this.handleLoginFromOtherTab(data);
        break;

      case 'closeNewsModels':
        if (!this.joyrideService.isTourInProgress()) {
          this.model.close();
        }
        break;

      case 'sessionTerminated':
        this.showSessionTerminationDialog(data.message);
        break;

      case 'forcedLogout':
      case 'logout':
        this.forcedLogout.next(true);
        this.clearSession();
        this.ngZone.run(() => {
          this.router.navigate(['/login']);
        });
        break;

      case 'sessionExtended':
        Swal.close();
        if (data.newToken) {
          this.updateToken(data.newToken, data.expiry);
        }
        this.toast.success('Session successfully extended.');

        this.ngZone.run(() => {
          this.disableApiCall = true;
          sessionStorage.setItem('session_reloading', 'true');
          window.location.reload();
        });
        break;

      case 'refreshAllTabs':
        this.ngZone.run(() => {
          this.disableApiCall = true;
          sessionStorage.setItem('session_reloading', 'true');
          window.location.reload();
        });
        break;

      case 'extensionAcknowledged':
        Swal.close();
        break;

      case 'tabOpened':
        this.activeTabs.add(data.tabId);

        this.bc.postMessage({
          type: 'tabActive',
          tabId: this.tabId,
          timestamp: Date.now(),
        });
        break;

      case 'tabClosed':
        this.activeTabs.delete(data.tabId);
        break;

      case 'getActiveTabs':
        this.bc.postMessage({
          type: 'tabActive',
          tabId: this.tabId,
          timestamp: Date.now(),
        });
        break;

      case 'tabActive':
        this.activeTabs.add(data.tabId);
        break;

      case 'ReceiveNewsNotification':
        this.handleGetNewsById(data.newsId);
        break;
    }
  }

  refreshAllTabs() {
    this.disableApiCall = true;
    sessionStorage.setItem('session_reloading', 'true');

    this.bc.postMessage({
      type: 'refreshAllTabs',
      tabId: this.tabId,
      timestamp: Date.now(),
    });
  }

  private handleLoginFromOtherTab(data: any) {
    if (data.token) {
      const expiry = new Date(data.expiry);
      this.updateToken(data.token, expiry);
    }

    this.loginFromOtherTab.next(true);

    this.ngZone.run(() => {
      if (!window.location.href.includes('/login')) {
        this.router.navigate(['/dashboard']);
      } else {
        setTimeout(() => {
          this.disableApiCall = true;
          sessionStorage.setItem('session_reloading', 'true');
          window.location.reload();
        }, 1000);
      }
    });
  }

  private updateToken(token: string, expiry: Date) {
    this.cookieHelper.updateTokenOnly(token, expiry);
    this.cookieHelper.updateExpiry(expiry);
  }

  handleSessionTermination(message: string) {
    this.showSessionTerminationDialog(message);
  }

  showSessionTerminationDialog(message: string) {
    let timerInterval: any;
    Swal.fire({
      title: SIMULTANEOUS_LOGIN_DETECTED_TITLE,
      html: SIMULTANEOUS_LOGIN_DETECTED_TEXT,
      icon: 'warning',
      timer: 10000,
      timerProgressBar: true,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      customClass: 'Popupbackground simultaneous-login',
      didOpen: () => {
        const timer = Swal.getPopup()?.querySelector('b');
        timerInterval = setInterval(() => {
          if (timer) timer.textContent = `${Math.ceil(Swal.getTimerLeft()! / 1000)}`;
        }, 1000);
      },
      willClose: () => {
        clearInterval(timerInterval);
      },
    });

    setTimeout(() => {
      Swal.close();
      this.bc.postMessage({
        type: 'logout',
        tabId: this.tabId,
        timestamp: Date.now(),
      });
      this.authFacade.logout();
    }, 9500);
  }

  logout() {
    this.bc.postMessage({
      type: 'logout',
      tabId: this.tabId,
      timestamp: Date.now(),
    });

    this.clearSession();
  }

  clearSession() {
    localStorage.removeItem('token');
    localStorage.removeItem('deviceId');
    localStorage.removeItem('lastTrackedUrl');
    localStorage.removeItem('session_extension_requested');
    localStorage.removeItem('session_termination_handling');
    sessionStorage.clear();
    this.cookieService.delete('token');
    this.authFacade.logout();
  }

  async getAccessModuleData() {
    const response = await this.authService.accessRoles();
    localStorage.setItem('access', JSON.stringify(response.data?.menus));
    localStorage.setItem('featureAccess', JSON.stringify(response.data?.featureAccess));
    const hideFeatureUpgrade = response.data?.hideFeatureUpgrade;
    this.featureConfigService.handleHideFeatureUpgrade(hideFeatureUpgrade);
    if (response.data?.latestNews) {
      setTimeout(() => {
        this.openAdditionalInfo(response.data?.latestNews);
      });
    }
    this.menuService.initializeMenu();
  }

  stopConnection() {
    if (this.hubConnection) {
      this.hubConnection.stop().catch((err: any) => console.error('SignalR stop error', err));
    }
  }

  logRouteActivities(payload: any) {
    const enhancedPayload = {
      ...payload,
      timestamp: new Date().toISOString(),
      tabId: this.tabId,
      deviceId: this.deviceId,
    };

    return this.http.post<any>(`${this.url}/Login/logPageActivity`, enhancedPayload);
  }

  ngOnDestroy() {
    if (this.hubConnection) this.hubConnection.stop();
    this.bc.close();
    this.unregisterTab();
  }
}
